import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/ledgerApi";
import { Loader2, Search, Save, CheckCircle2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const STATUS_BADGE = {
  off_menu: { label: "off menu", cls: "text-[rgba(240,233,216,0.45)] border-[rgba(240,233,216,0.2)]" },
};

function ProductToggleRow({ product, changed, onToggle }) {
  const badge = STATUS_BADGE[product.status];
  return (
    <div
      data-testid={`master-row-${product.id}`}
      className={`flex items-center gap-3 py-3 border-b border-[rgba(179,145,74,0.15)] transition-opacity ${
        product.available ? "" : "opacity-45"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-body text-[var(--hoa-cream-50)] text-sm sm:text-base leading-snug">{product.name}</p>
        <div className="flex items-center gap-2 mt-1">
          {badge && (
            <span className={`inline-block font-label text-[7.5px] tracking-[0.2em] uppercase border rounded-full px-2 py-0.5 ${badge.cls}`}>
              {badge.label}
            </span>
          )}
          {!product.available && (
            <span
              data-testid={`master-hidden-badge-${product.id}`}
              className="inline-block font-label text-[7.5px] tracking-[0.2em] uppercase border border-[rgba(201,111,90,0.5)] text-[#c96f5a] rounded-full px-2 py-0.5"
            >
              hidden everywhere
            </span>
          )}
          {changed && (
            <span
              data-testid={`master-unsaved-badge-${product.id}`}
              className="inline-block font-label text-[7.5px] tracking-[0.2em] uppercase border border-[rgba(179,145,74,0.6)] text-[var(--hoa-gold-300)] rounded-full px-2 py-0.5"
            >
              not saved yet
            </span>
          )}
        </div>
      </div>
      <Switch
        data-testid={`master-toggle-${product.id}`}
        checked={product.available}
        onCheckedChange={(v) => onToggle(product.id, v)}
        className="data-[state=checked]:bg-[var(--hoa-gold-400)] data-[state=unchecked]:bg-[rgba(240,233,216,0.2)]"
        aria-label={`Availability of ${product.name}`}
      />
    </div>
  );
}

export const MasterProducts = ({ onAuthError }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const baseline = useRef({});
  const flashTimer = useRef(null);

  useEffect(() => {
    api
      .get("/admin/products/master")
      .then((res) => {
        const cats = res.data || [];
        const map = {};
        cats.forEach((c) => c.products.forEach((p) => (map[p.id] = p.available)));
        baseline.current = map;
        setCategories(cats);
      })
      .catch((e) => onAuthError && onAuthError(e))
      .finally(() => setLoading(false));
    return () => clearTimeout(flashTimer.current);
  }, [onAuthError]);

  const counts = useMemo(() => {
    const all = categories.flatMap((c) => c.products);
    return { total: all.length, available: all.filter((p) => p.available).length };
  }, [categories]);

  const pending = useMemo(
    () =>
      categories
        .flatMap((c) => c.products)
        .filter((p) => baseline.current[p.id] !== p.available)
        .map((p) => ({ product_id: p.id, available: p.available })),
    [categories]
  );

  const toggle = (id, available) => {
    setSavedFlash(false);
    setSaveError(false);
    setCategories((cats) =>
      cats.map((c) => ({
        ...c,
        products: c.products.map((p) => (p.id === id ? { ...p, available } : p)),
      }))
    );
  };

  const save = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      await api.post("/admin/products/availability/bulk", { changes: pending });
      pending.forEach((ch) => (baseline.current[ch.product_id] = ch.available));
      setCategories((cats) => [...cats]);
      setSavedFlash(true);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setSavedFlash(false), 4000);
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) onAuthError(e);
      else setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="master-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const visible = categories
    .map((c) => ({ ...c, products: q ? c.products.filter((p) => p.name.toLowerCase().includes(q)) : c.products }))
    .filter((c) => c.products.length > 0);

  return (
    <div data-testid="master-products" className="mt-8 pb-28">
      <p className="font-body text-[rgba(240,233,216,0.6)] text-sm" data-testid="master-availability-count">
        <span className="text-[var(--hoa-gold-300)]">{counts.available}</span> of {counts.total} bakes available
      </p>

      {savedFlash && pending.length === 0 && (
        <p
          data-testid="master-saved-confirmation"
          className="mt-3 inline-flex items-center gap-2 font-body text-sm text-[var(--hoa-gold-300)]"
        >
          <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} /> Saved — the house menu is updated.
        </p>
      )}
      {saveError && (
        <p data-testid="master-save-error" className="mt-3 font-body text-sm text-[#c96f5a]">
          Could not save the changes. Please try again.
        </p>
      )}

      <div className="relative mt-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgba(240,233,216,0.4)]" strokeWidth={1.6} />
        <input
          data-testid="master-search-input"
          className="w-full h-12 pl-11 pr-4 rounded-[12px] bg-[rgba(240,233,216,0.05)] border border-[rgba(179,145,74,0.35)] font-body text-sm text-[var(--hoa-cream-50)] placeholder:text-[rgba(240,233,216,0.35)] focus:outline-none focus:border-[var(--hoa-gold-400)] transition-colors"
          placeholder="Search a bake…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {visible.map((cat) => (
        <div key={cat.category} className="mt-6">
          <div className="flex items-center gap-3">
            <span className="font-label text-[9px] tracking-[0.26em] uppercase hoa-gold-text">{cat.category}</span>
            <span className="flex-1 h-px bg-[rgba(179,145,74,0.2)]" />
          </div>
          <div className="mt-1">
            {cat.products.map((p) => (
              <ProductToggleRow
                key={p.id}
                product={p}
                changed={baseline.current[p.id] !== p.available}
                onToggle={toggle}
              />
            ))}
          </div>
        </div>
      ))}

      {visible.length === 0 && (
        <p className="font-body text-[rgba(240,233,216,0.5)] text-sm mt-8" data-testid="master-empty">
          No bakes match your search.
        </p>
      )}

      {pending.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-auto sm:sticky sm:bottom-6 z-40 mx-auto max-w-[560px]">
          <button
            data-testid="master-save-button"
            onClick={save}
            disabled={saving}
            className="w-full h-14 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_12px_36px_rgba(0,0,0,0.45)] hover:brightness-110 transition-[filter] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" strokeWidth={1.8} />
                Save changes · {pending.length}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
